import React, { createRef, useEffect, useMemo, useState } from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  ActionIconContainer,
  AddFormButton,
  FilterButton,
} from "../../../shared/components/TableElements";
import {
  FormItem,
  HintText,
} from "../../../shared/components/form/FormElements";
import { formInputStyles } from "../../../shared/constants/styles";
import { colorRed } from "../../../utils/palette";
import { useSelector } from "react-redux";
import {
  handleNegative,
  handleNumberInputOnWheelPreventChange,
} from "../../../shared/helpers";
import Error from "@/shared/components/form/Error";
import { RowContainer } from "./AddPartsPopupNew";

// Refactored version — uses taskBatches + onBatchesChange + onTaskCostsChange
// instead of partBatches/setPartBatches/serviceTasksRef.
const UnitsConsumedPopupNew = ({
  showPopup,
  setShowPopup,
  taskBatches = [], // confirmed batches for this task (from parent serviceTasks state)
  sessionBatches = [], // all batches added in the current add-parts session (never in parent until Submit)
  onSessionUpdate, // (localBatches) => void — called when navigating to "+Select Parts"
  onSessionClear,  // () => void — called on Close or Submit to end the session
  onBatchesChange, // (newBatches: array) => void — called only on Submit
  onTaskCostsChange, // ({ partsCost, totalCost, partsDisplayArr, noOfParts, laborCost }) => void
  serviceTaskDetailId,
  setShowAddPartsPopup,
  setFromUnitsConsumed,
  serviceTasksChanged,
  setServiceTasksChanged,
}) => {
  const { t } = useTranslation("common");
  // Local copy — only synced to parent on Submit
  const [localBatches, setLocalBatches] = useState([]);
  const [batchRefs, setBatchRefs] = useState({});
  const [errInput, setErrInput] = useState("");
  const [errMsg, setErrMsg] = useState("");

  // Re-initialize local state each time the popup opens
  useEffect(() => {
    if (showPopup) {
      const batches = [...(taskBatches || []), ...(sessionBatches || [])];
      setLocalBatches(batches);
      const refs = {};
      batches
        .filter((b) => b.action !== 3)
        .forEach((batch) => {
          refs[batch.parts_batch_id] = { ref: createRef() };
        });
      setBatchRefs(refs);
      setErrInput("");
      setErrMsg("");
    }
  }, [showPopup]); // intentionally only re-init when popup opens, not on every taskBatches change

  const selectedBatches = useMemo(
    () => localBatches.filter((b) => b?.action !== 3),
    [localBatches],
  );

  const handleClose = () => {
    onSessionClear();
    setShowPopup(false);
  };

  const handleUnitsConsumedChange = (e, partsBatchId) => {
    e.preventDefault();
    setErrInput("");
    const { value } = e.target;

    setLocalBatches((prev) =>
      prev.map((b) =>
        b.parts_batch_id === partsBatchId
          ? {
              ...b,
              unitsConsumed: +value || "",
              totalCost: (+b.purchase_cost_per_unit * +value)?.toFixed(2),
              availableQuantity: +b.currentlyAvailableQuantity - +value,
            }
          : b,
      ),
    );
    setErrMsg("");
  };

  const handleSubmit = () => {
    // Validate
    for (let batch of selectedBatches) {
      const { parts_batch_id } = batch;
      if (!batch.unitsConsumed || batch.unitsConsumed <= 0) {
        setErrInput(parts_batch_id);
        setBatchRefs((prev) => {
          let temp = { ...prev };
          temp[parts_batch_id].ref.current.focus();
          return temp;
        });
        setErrMsg(t("units_consumed_less_than_zero_err"));
        return;
      } else if (batch.availableQuantity < 0) {
        setErrInput(parts_batch_id);
        setBatchRefs((prev) => {
          let temp = { ...prev };
          temp[parts_batch_id].ref.current.focus();
          return temp;
        });
        setErrMsg(t("consumed_qty_more_than_avail_qty_err"));
        return;
      }
    }

    // Sync validated local state to parent
    onBatchesChange(localBatches);

    // Compute costs from local state
    const activeBatches = localBatches.filter((b) => b.action !== 3);
    const totalPartsCost = activeBatches
      .map((b) => +b.totalCost)
      .filter((c) => +c)
      .reduce((acc, c) => acc + c, 0);

    const noOfParts = [...new Set(activeBatches.map((b) => b.part_id))].length;

    onTaskCostsChange({
      partsCost: +totalPartsCost,
      totalCost: null, // parent computes: +laborCost + +totalPartsCost
      noOfParts,
    });

    if (!serviceTasksChanged) {
      setServiceTasksChanged(true);
    }

    onSessionClear();
    setShowPopup(false);
  };

  const handleRemoveBatch = (batch) => {
    setLocalBatches((prev) =>
      prev.reduce((result, b) => {
        if (b.parts_batch_id === batch.parts_batch_id) {
          if (b.action === 2) {
            result.push({ ...b, action: 3 });
          }
          // action:1 batches are dropped (never in DB)
        } else {
          result.push(b);
        }
        return result;
      }, []),
    );
  };

  return (
    <Modal show={showPopup} size="xl">
      <Modal.Header>
        <h4>{t("parts_consumed")}</h4>
        <div className="d-flex justify-content-end">
          <ActionIconContainer onClick={handleClose}>
            <i className="fi fi-rr-cross-small" />
          </ActionIconContainer>
        </div>
      </Modal.Header>
      <Modal.Body className="m-0 p-0">
        <Container className="m-0 p-0">
          <Row className="mt-2 mx-2">
            <Col md={3}></Col>
            <Col md={1}>
              <p style={{ fontSize: 12, fontWeight: 300 }}>{t("batch_id")}</p>
            </Col>
            <Col md={1}>
              <p style={{ fontSize: 12, fontWeight: 300 }}>{t("location")}</p>
            </Col>
            <Col md={1}>
              <p style={{ fontSize: 12, fontWeight: 300 }}>{t("available")}</p>
            </Col>
            <Col md={3}>
              <p style={{ fontSize: 12, fontWeight: 300 }}>{t("consumed")}</p>
            </Col>
            <Col md={1}>
              <p style={{ fontSize: 12, fontWeight: 300 }}>
                {t("cost_per_unit")}
              </p>
            </Col>
            <Col md={1}>
              <p style={{ fontSize: 12, fontWeight: 300 }}>{t("tc_tv")}</p>
            </Col>
            <Col md={1}></Col>
          </Row>
          <RowContainer $height={`${window.innerHeight - 350}px`}>
            {selectedBatches.length === 0 && (
              <Row
                className="d-flex justify-content-center align-items-center"
                style={{ height: "-webkit-fill-available" }}
              >
                <Col md={2}>
                  <p>{t("no_records")}</p>
                </Col>
              </Row>
            )}
            {selectedBatches.map((batch, i) => (
              <React.Fragment key={batch.parts_batch_id}>
                <Row
                  className="m-2 py-2 d-flex align-items-center"
                  tabIndex={i + 1}
                >
                  <Col md={3}>
                    <div className="d-flex flex-column justify-content-center">
                      <p>{batch.part_name}</p>
                      {batch.part_number && (
                        <HintText>{`${t("part_no")}: ${batch.part_number}`}</HintText>
                      )}
                    </div>
                  </Col>
                  <Col md={1}>
                    <p style={{ position: "relative", right: "0.4rem" }}>
                      {batch.batch_id}
                    </p>
                  </Col>
                  <Col md={1}>
                    <p style={{ position: "relative", right: "0.4rem" }}>
                      {batch.location}
                    </p>
                  </Col>
                  <Col md={1}>
                    <p>{batch.availableQuantity}</p>
                  </Col>
                  <Col md={3} className="d-flex align-items-center">
                    <FormItem
                      style={{
                        borderColor:
                          errInput === batch.parts_batch_id ? colorRed : "",
                      }}
                    >
                      <input
                        ref={batchRefs[batch.parts_batch_id]?.ref}
                        name={`units-consumed-${batch.parts_batch_id}`}
                        type="number"
                        onWheel={handleNumberInputOnWheelPreventChange}
                        value={batch.unitsConsumed}
                        onChange={(e) =>
                          handleUnitsConsumedChange(e, batch.parts_batch_id)
                        }
                        onKeyDown={(e) => handleNegative(e)}
                        style={formInputStyles}
                      />
                    </FormItem>
                  </Col>
                  <Col md={1}>
                    <p style={{ marginLeft: "0.4rem" }}>
                      {batch.purchase_cost_per_unit}
                    </p>
                  </Col>
                  <Col md={1}>
                    <p style={{ marginLeft: "0.5rem" }}>{batch?.totalCost}</p>
                  </Col>
                  <Col md={1}>
                    <ActionIconContainer
                      onClick={() => handleRemoveBatch(batch)}
                    >
                      <i className="fi fi-rr-cross-small" />
                    </ActionIconContainer>
                  </Col>
                </Row>
              </React.Fragment>
            ))}
          </RowContainer>
          {errMsg && (
            <Row className="m-2">
              <Col>
                <Error error={errMsg} />
              </Col>
            </Row>
          )}
          <Row className="mx-2 my-3">
            <Col>
              <FilterButton
                onClick={() => {
                  // Persist current local state into sessionBatches so it survives the round-trip
                  onSessionUpdate(localBatches);
                  setFromUnitsConsumed(true);
                  setShowAddPartsPopup(true);
                  setShowPopup(false);
                }}
              >{`+${t("select_parts")}`}</FilterButton>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <AddFormButton
          id="submit-form-button"
          type="button"
          onClick={handleSubmit}
        >
          {t("submit")}
        </AddFormButton>
      </Modal.Footer>
    </Modal>
  );
};

export default UnitsConsumedPopupNew;
